/**
 * Live preview voor het Onderwater venster (§2.3 in MVP-scope).
 *
 * Twee render-routes:
 *
 * - **Mermaid** — fully client-side via dynamic-import van `mermaid` (npm,
 *   ~700 KB). Het pakket wordt pas geladen wanneer de gebruiker daadwerkelijk
 *   de Mermaid-modus opent + de preview aanzet, zodat de PlantUML-only
 *   gebruiker de bundle nooit binnenhaalt. Render is instant want geen
 *   roundtrip; debouncen we toch met 300 ms zodat tikken niet elke keystroke
 *   triggert.
 * - **PlantUML** — server-side via `/api/v1/render` (Kroki-proxy in §3.8).
 *   Cache-laag op de worker zorgt dat tweede renders van dezelfde tekst
 *   instant teruggegeven worden. Debounce 500 ms: PlantUML-renders kosten
 *   ergens tussen 100 ms (cache hit) en enkele seconden (cache miss bij
 *   complex diagram), dus we gunnen de gebruiker ruimte om door te typen.
 *
 * In beide routes wordt de output als **inline SVG** in een scrollbaar
 * paneel getoond. Render-fouten verschijnen als amber-strook bovenaan met
 * de raw error-text van Mermaid / het worker-Problem-Detail. De preview
 * staat naast de editor in een 50/50 horizontale split (zie TextPane).
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ApiError, api } from "../../api/client";

const PLANTUML_DEBOUNCE_MS = 500;
const MERMAID_DEBOUNCE_MS = 300;

type Notation = "plantuml" | "mermaid";

interface PreviewPaneProps {
  text: string;
  notation: Notation;
}

/**
 * Cached promise voor het lazy-loaden van mermaid. We willen de
 * dynamic-import maar één keer doen, en daarna direct de geïnitialiseerde
 * `mermaid` namespace teruggeven. Een module-level Promise houdt dat
 * eenvoudig zonder useRef-gepruts in de component.
 */
let mermaidLoadPromise: Promise<typeof import("mermaid").default> | null = null;
function loadMermaid(): Promise<typeof import("mermaid").default> {
  if (!mermaidLoadPromise) {
    mermaidLoadPromise = import("mermaid").then((mod) => {
      const m = mod.default;
      m.initialize({
        startOnLoad: false,
        securityLevel: "loose",
        theme: "default",
        // Mermaid 12 kiest standaard ELK en een nieuwe opmaak; zo blijft de
        // preview er uitzien als in Mermaid 11.
        layout: "dagre",
        look: "classic",
      });
      return m;
    });
  }
  return mermaidLoadPromise;
}

const MIN_ZOOM = 0.1;
const MAX_ZOOM = 8;
const ZOOM_STEP = 1.2; // klik op +/- vermenigvuldigt/deelt met deze factor

export function PreviewPane({ text, notation }: PreviewPaneProps) {
  const { t } = useTranslation();
  const [svg, setSvg] = useState<string>("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  // Zoom + pan state — gedraagt zich vergelijkbaar met React Flow's
  // canvas-viewport: scroll-wiel zoomt, slepen pant. Geen reset bij
  // nieuwe SVG zodat de gebruiker tijdens het tweaken van een diagram
  // niet steeds opnieuw naar 'm toe hoeft te scrollen.
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const isPanning = useRef(false);
  const lastPointer = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  // Last-render-token: every debounce-fire bumps this; the async render
  // checks it before committing state so an in-flight render that finishes
  // AFTER a newer one started gets discarded (no flicker / no rollback).
  const tokenRef = useRef(0);

  useEffect(() => {
    if (!text.trim()) {
      setSvg("");
      setError(null);
      setLoading(false);
      return;
    }

    const myToken = ++tokenRef.current;
    setLoading(true);
    const debounceMs = notation === "mermaid" ? MERMAID_DEBOUNCE_MS : PLANTUML_DEBOUNCE_MS;
    const timer = setTimeout(async () => {
      try {
        if (notation === "mermaid") {
          const mermaid = await loadMermaid();
          if (myToken !== tokenRef.current) return;
          // Mermaid v10+ uses `render(id, source)` and returns `{ svg, ... }`.
          // Strip our own `%% type:archimate.X` comments — Mermaid will skip
          // them too but we don't need them in the rendered output.
          const id = `ae-mermaid-preview-${myToken}`;
          const result = await mermaid.render(id, text);
          if (myToken !== tokenRef.current) return;
          setSvg(result.svg);
          setError(null);
        } else {
          const { blob, contentType } = await api.render({
            source: text,
            format: "plantuml",
            outputType: "svg",
          });
          if (myToken !== tokenRef.current) return;
          if (!contentType.includes("svg")) {
            setError(t("textPane.preview.unexpectedContentType", { contentType }));
            setSvg("");
          } else {
            const text = await blob.text();
            if (myToken !== tokenRef.current) return;
            setSvg(text);
            setError(null);
          }
        }
      } catch (err) {
        if (myToken !== tokenRef.current) return;
        if (err instanceof ApiError) {
          setError(err.body?.detail ?? err.body?.title ?? err.message);
        } else if (err instanceof Error) {
          setError(err.message);
        } else {
          setError(t("textPane.preview.unknownError"));
        }
      } finally {
        if (myToken === tokenRef.current) setLoading(false);
      }
    }, debounceMs);

    return () => {
      clearTimeout(timer);
    };
  }, [text, notation, t]);

  // Zoom-om-cursor: bij scroll-wiel kiezen we het schaal-anker op de
  // cursor-positie zodat het punt onder de muis stil blijft (idem
  // React Flow). Wiskunde:
  //   contentCoord = (cursor - pan) / zoom
  //   nieuwePan = cursor - contentCoord * nieuweZoom
  const handleWheel = useCallback((e: React.WheelEvent<HTMLDivElement>) => {
    if (!viewportRef.current) return;
    e.preventDefault();
    const rect = viewportRef.current.getBoundingClientRect();
    const cx = e.clientX - rect.left;
    const cy = e.clientY - rect.top;
    setZoom((prevZoom) => {
      const factor = e.deltaY < 0 ? ZOOM_STEP : 1 / ZOOM_STEP;
      const next = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, prevZoom * factor));
      if (next === prevZoom) return prevZoom;
      setPan((prevPan) => {
        const contentX = (cx - prevPan.x) / prevZoom;
        const contentY = (cy - prevPan.y) / prevZoom;
        return { x: cx - contentX * next, y: cy - contentY * next };
      });
      return next;
    });
  }, []);

  const handlePointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    // Alleen linker-muisknop (button 0) en middel (1) pant; rechts laten
    // we vrij voor evt. context-menu.
    if (e.button !== 0 && e.button !== 1) return;
    isPanning.current = true;
    lastPointer.current = { x: e.clientX, y: e.clientY };
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  }, []);

  const handlePointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (!isPanning.current) return;
    const dx = e.clientX - lastPointer.current.x;
    const dy = e.clientY - lastPointer.current.y;
    lastPointer.current = { x: e.clientX, y: e.clientY };
    setPan((p) => ({ x: p.x + dx, y: p.y + dy }));
  }, []);

  const handlePointerUp = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (!isPanning.current) return;
    isPanning.current = false;
    try {
      (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
    } catch {
      // pointer capture kan al verloren zijn — negeer
    }
  }, []);

  const zoomIn = useCallback(() => {
    setZoom((z) => Math.min(MAX_ZOOM, z * ZOOM_STEP));
  }, []);
  const zoomOut = useCallback(() => {
    setZoom((z) => Math.max(MIN_ZOOM, z / ZOOM_STEP));
  }, []);
  // Fit-to-pane: meet de natuurlijke SVG-grootte + viewport, kies de
  // grootste schaal waarbij beide assen passen, en centreer de SVG.
  const fitToPane = useCallback(() => {
    const viewport = viewportRef.current;
    const content = contentRef.current;
    if (!viewport || !content) return;
    const svgEl = content.querySelector("svg");
    if (!svgEl) return;
    const vw = viewport.clientWidth;
    const vh = viewport.clientHeight;
    // Natuurlijke SVG-dimensies uit het viewBox (Kroki zet 'm altijd);
    // val terug op width/height-attributen als viewBox ontbreekt.
    let sw = 0;
    let sh = 0;
    const vb = svgEl.getAttribute("viewBox");
    if (vb) {
      const parts = vb.split(/\s+/).map(Number);
      if (parts.length === 4 && Number.isFinite(parts[2]!) && Number.isFinite(parts[3]!)) {
        sw = parts[2]!;
        sh = parts[3]!;
      }
    }
    if (!sw || !sh) {
      sw = Number.parseFloat(svgEl.getAttribute("width") ?? "0") || svgEl.clientWidth;
      sh = Number.parseFloat(svgEl.getAttribute("height") ?? "0") || svgEl.clientHeight;
    }
    if (!sw || !sh) return;
    const scale = Math.min(vw / sw, vh / sh, MAX_ZOOM);
    const clampedScale = Math.max(MIN_ZOOM, scale);
    setZoom(clampedScale);
    setPan({
      x: (vw - sw * clampedScale) / 2,
      y: (vh - sh * clampedScale) / 2,
    });
  }, []);
  const resetView = useCallback(() => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  }, []);

  return (
    <div className="flex h-full min-h-0 flex-col border-(--ld-border) border-l bg-(--ld-surface)">
      <div className="flex shrink-0 items-center justify-between border-(--ld-border) border-b bg-(--ld-raised) px-3 py-1 text-xs text-(--ld-muted)">
        <span>
          {t("textPane.preview.header", {
            notation: notation === "mermaid" ? "Mermaid" : "PlantUML",
          })}
        </span>
        <div className="flex items-center gap-2">
          {loading && <span className="text-(--ld-accent)">{t("textPane.preview.rendering")}</span>}
          <div className="flex items-center gap-0.5">
            <button
              type="button"
              className="rounded border border-(--ld-control) bg-(--ld-surface) px-1.5 py-0.5 font-semibold text-(--ld-text) text-xs leading-none hover:bg-(--ld-raised)"
              onClick={zoomOut}
              title={t("zoomPan.zoomOut")}
              aria-label={t("zoomPan.zoomOut")}
            >
              −
            </button>
            <button
              type="button"
              className="rounded border border-(--ld-control) bg-(--ld-surface) px-1.5 py-0.5 font-mono text-(--ld-text) text-xs leading-none hover:bg-(--ld-raised)"
              onClick={resetView}
              title={t("textPane.preview.resetTitle")}
            >
              {Math.round(zoom * 100)}%
            </button>
            <button
              type="button"
              className="rounded border border-(--ld-control) bg-(--ld-surface) px-1.5 py-0.5 font-semibold text-(--ld-text) text-xs leading-none hover:bg-(--ld-raised)"
              onClick={zoomIn}
              title={t("zoomPan.zoomIn")}
              aria-label={t("zoomPan.zoomIn")}
            >
              +
            </button>
            <button
              type="button"
              className="ml-1 rounded border border-(--ld-control) bg-(--ld-surface) px-1.5 py-0.5 text-(--ld-text) text-xs leading-none hover:bg-(--ld-raised)"
              onClick={fitToPane}
              title={t("textPane.preview.fitTitle")}
            >
              ⤢
            </button>
          </div>
        </div>
      </div>
      {error && (
        <div className="shrink-0 border-transparent border-b bg-(--ld-notice-bg) px-3 py-1.5 font-mono text-(--ld-notice-ink) text-xs">
          {error}
        </div>
      )}
      <div
        ref={viewportRef}
        // Zelfde achtergrond als het canvas (zie .ae-diagram-surface in globals.css).
        className="ae-diagram-surface relative min-h-0 flex-1 overflow-hidden"
        onWheel={handleWheel}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        style={{ cursor: svg ? (isPanning.current ? "grabbing" : "grab") : "default" }}
      >
        {svg ? (
          // eslint-disable-next-line react/no-danger
          // De SVG behoudt z'n natuurlijke grootte; pan + zoom worden
          // toegepast via CSS-transform op de wrapper. transform-origin
          // op linker-bovenhoek (0 0) zodat de zoom-om-cursor-wiskunde
          // in handleWheel klopt.
          <div
            ref={contentRef}
            className="absolute top-0 left-0"
            style={{
              transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
              transformOrigin: "0 0",
            }}
            dangerouslySetInnerHTML={{ __html: svg }}
          />
        ) : !error && !loading ? (
          <p className="px-2 py-4 text-[#64748b] text-xs italic">{t("textPane.preview.empty")}</p>
        ) : null}
      </div>
    </div>
  );
}
